import _mergeWith from 'lodash/mergeWith';
import { fromEntries } from 'utils/core';

export function isObject(obj) {
    return Object(obj) === obj;
}

export function isEmptyObject(obj) {
    return Object.keys(obj).length === 0;
}

export function isJSONParsable(str = '') {
    try {
        JSON.parse(str);
    } catch (err) {
        return false;
    }
    return true;
}

export function parseJSON_ifParsable(target = '', fallbackVal = target) {
    try {
        return JSON.parse(target);
    } catch (err) {
        return fallbackVal;
    }
}

export function objFirstLevelJSONStringify(obj = {}) {
    // 這個用來專門刪除 setting key 的 key name: "deleted" 比較特別
    // 值需要維持是 array 的型別來給後端做處理
    const DELETED_KEY = 'deleted';

    return Object.entries(obj).reduce((newObj, [key, val]) => {
        // 因除了它以外的資料都是要被儲存下來的，因此需要 stringify 才能儲存
        val = isObject(val) && key !== DELETED_KEY ? JSON.stringify(val) : val;
        newObj[key] = val;

        return newObj;
    }, {});
}

export function objFirstLevelJSONParse(obj = {}) {
    return Object.entries(obj).reduce((newObj, [key, val]) => {
        val = parseJSON_ifParsable(val);
        newObj[key] = val;

        return newObj;
    }, {});
}

export const reduxObjKeysUpdateActions = {
    MERGE: 'merge',
    REPLACE: 'replace'
};
export function updateReduxObjKeyHandleDelete(
    currentState,
    newState,
    updateAction = reduxObjKeysUpdateActions.MERGE
) {
    const currentDeletedKeyArray = currentState?.deleted ? [...currentState.deleted] : [];
    const newDeletedKeyArray = currentDeletedKeyArray.filter(
        deletedKey => newState[deletedKey] === undefined
    );
    const nextState =
        updateAction === reduxObjKeysUpdateActions.MERGE
            ? _mergeWith(
                  {},
                  currentState,
                  newState,
                  { deleted: newDeletedKeyArray },
                  (_, srcVal) => {
                      if (Array.isArray(srcVal)) {
                          return srcVal;
                      }
                  }
              )
            : { ...currentState, ...newState, deleted: newDeletedKeyArray };

    return nextState;
}

export function deleteReduxObjKey(state = {}, deleted = []) {
    const newState = { ...state };
    const newDeletedSet = Array.isArray(state.deleted) ? new Set([...state.deleted]) : new Set([]);

    deleted.forEach(wantToDeleteKeyName => {
        if (newState[wantToDeleteKeyName] !== undefined) {
            delete newState[wantToDeleteKeyName];
            newDeletedSet.add(wantToDeleteKeyName);
        }
    });

    return {
        ...newState,
        deleted: [...newDeletedSet]
    };
}

// Match prefix or suffix in a key
const matchKey = (key, { prefixes = [], suffixes = [] }) =>
    prefixes.some(prefix => key.startsWith(prefix)) ||
    suffixes.some(suffix => key.endsWith(suffix));

// Recursive API response transformer
const typeConversion = (
    key,
    value,
    { prefixes = ['enable'], suffixes = ['Id', 'id'], Constructor = Number }
) => {
    if (typeof value === 'string' && matchKey(key, { prefixes, suffixes })) {
        const convertedValue = Constructor(value);
        return !isNaN(convertedValue) ? { [key]: convertedValue } : { [key]: value };
    }
    return { [key]: value };
};

// Recursive API response transformer
export const transformApiResponse = (apiResp = {}, options = {}) => {
    const transform = obj =>
        Object.entries(obj).reduce((acc, [key, value]) => {
            acc[key] =
                typeof value === 'object' && value !== null
                    ? transform(value)
                    : typeConversion(key, value, options)[key];
            return acc;
        }, {});
    return transform(apiResp);
};

export function reverseObj(obj) {
    return fromEntries(Object.entries(obj).map(([key, value]) => [value, key]));
}

export function isNonEmptyObject(value) {
    return (
        typeof value === 'object' &&
        value !== null &&
        !Array.isArray(value) &&
        Object.keys(value).length > 0 // Check if it has at least one own enumerable property
    );
}

/**
 * 檢查指定的登入方式是否在資料陣列中有設定值
 * @example
 * // data = [{line:{enterprise:'maac'}},{facebook:{enterprise:'maac'}}]
 * // loginMethod = 'line'
 * // returns [true,false]
 * @param {Array} data - 包含登入方式設定的物件陣列
 * @param {string} loginMethod - 要檢查的登入方式 (例如: 'line', 'enterprise')
 * @returns {Array<boolean>} 布林值陣列，表示每個項目是否有設定該登入方式
 */
export const isEnableLoginMethodObject = (data = [], loginMethod) => {
    return data.map(value => {
        if (!value) return false;

        const parseValue = parseJSON_ifParsable(value);
        if (Boolean(parseValue[loginMethod])) {
            return true;
        }
        return false;
    });
};

/**
 * 檢查指定的登入方式是否在資料陣列中有設定值
 * @example
 * // data = {line:{enterprise:'maac'}
 * // loginMethod = 'line'
 * // settingLoginMethod = 'enterprise'
 * // returns {enterprise:'maac'}
 * @param {Object} data - 包含登入方式設定的物件
 * @param {string} loginMethod - 指定登入方式 (例如: 'line', 'enterprise')
 * @param {string} settingLoginMethod - 指定登入方式要使用的對應的登入方式 (例如: 'line', 'enterprise')
 * @returns {Object} 包含登入方式設定的物件
 */
export const getLoginMethodObjectValue = (data, loginMethod, settingLoginMethod) => {
    if (!data) return undefined;

    const parseValue = parseJSON_ifParsable(data);
    return parseValue?.[loginMethod]?.[settingLoginMethod];
};
